import { ref } from 'vue'

export interface LoadingOperation {
  name: string
  promise: Promise<any>
  onSuccess?: (result: any) => void
  onError?: (error: any) => void
}

export function useLoadingState() {
  const loadingStates = ref<Record<string, boolean>>({})
  const errors = ref<Record<string, string | null>>({})

  const isLoading = (name?: string) => {
    if (name) {
      return !!loadingStates.value[name]
    }
    return Object.values(loadingStates.value).some(loading => loading)
  }

  const getError = (name: string) => {
    return errors.value[name] || null
  }

  const clearError = (name: string) => {
    delete errors.value[name]
  }

  const executeWithLoading = async <T>(
    name: string,
    operation: () => Promise<T>,
    options?: {
      onSuccess?: (result: T) => void
      onError?: (error: any) => void
      clearErrorOnStart?: boolean
    }
  ): Promise<T | null> => {
    try {
      if (options?.clearErrorOnStart !== false) {
        clearError(name)
      }
      
      loadingStates.value[name] = true
      const result = await operation()
      
      if (options?.onSuccess) {
        options.onSuccess(result)
      }
      
      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred'
      errors.value[name] = errorMessage
      
      if (options?.onError) {
        options.onError(error)
      }
      
      throw error
    } finally {
      loadingStates.value[name] = false
    }
  }

  const setLoading = (name: string, loading: boolean) => {
    loadingStates.value[name] = loading
  }

  const setError = (name: string, error: string | null) => {
    errors.value[name] = error
  }

  return {
    isLoading,
    getError,
    clearError,
    executeWithLoading,
    setLoading,
    setError
  }
}